import React, { useEffect, useRef, useState } from 'react';
function fillVariables(text = '') {
  const samples = { 1: 'John', 2: '#12345', 3: 'tomorrow 2 PM', 4: 'Blastmate', 5: '10% off' };
  return String(text).replace(/\{\{(\d+)\}\}/g, (_, n) => samples[n] || `value${n}`);
}

/** When template has IMAGE/VIDEO/etc. from Meta, API does not expose a browser URL — only a placeholder until user adds a link (e.g. campaign). */
function MediaHeaderPlaceholder({ kind }) {
  const label =
    kind === 'video' ? 'Video header' : kind === 'document' ? 'Document header' : kind === 'audio' ? 'Audio header' : 'Image header';
  const hint =
    'Approved in Meta — no preview URL. For campaigns, paste a public https link to the file you want to send.';
  return (
    <div
      style={{
        width: '100%',
        minHeight: 120,
        background: 'linear-gradient(180deg, #e8ecef 0%, #dfe5e8 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '14px 12px',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ fontSize: 28, lineHeight: 1, opacity: 0.85 }} aria-hidden>
        {kind === 'video' ? '🎬' : kind === 'document' ? '📄' : kind === 'audio' ? '🎵' : '🖼'}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#3b4a54' }}>{label}</div>
      <div style={{ fontSize: 11, color: '#667781', textAlign: 'center', lineHeight: 1.35, maxWidth: 180 }}>{hint}</div>
    </div>
  );
}

const TemplatePreview = ({ header, body, footer, buttons, media }) => {
  const bodyText = fillVariables(body || '').trim() || 'Your message preview will appear here…';
  const mediaKind = (media?.type || '').toLowerCase();
  const mediaLink = (media?.link || '').trim();
  const showMediaFromLink = mediaKind && mediaLink;
  const showMediaPlaceholder = mediaKind && !mediaLink && ['image', 'video', 'document', 'audio'].includes(mediaKind);
  const chatAreaRef = useRef(null);
  const [scrollState, setScrollState] = useState({ up: false, down: false });
  const hasInteractedRef = useRef(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const dragStateRef = useRef({ active: false, startY: 0, startScrollTop: 0, pointerId: null });
  const [isDragging, setIsDragging] = useState(false);
  const supportsPointerEvents = typeof window !== 'undefined' && 'PointerEvent' in window;

  useEffect(() => {
    hasInteractedRef.current = false;
    setShowScrollHint(false);

    const el = chatAreaRef.current;
    if (!el) return;

    const updateScrollState = () => {
      const canScrollDown = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      const canScrollUp = el.scrollTop > 1;
      setScrollState({ up: canScrollUp, down: canScrollDown });
      if (!hasInteractedRef.current && (el.scrollTop > 1 || canScrollUp)) {
        hasInteractedRef.current = true;
        setShowScrollHint(false);
      }
    };

    updateScrollState();
    el.addEventListener('scroll', updateScrollState);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(el);
    }

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      resizeObserver?.disconnect();
    };
  }, [bodyText, header, footer, buttons, media]);

  const scrollByAmount = (offset) => {
    const el = chatAreaRef.current;
    if (!el) return;
    el.scrollBy({ top: offset, behavior: 'smooth' });
  };

  useEffect(() => {
    if ((scrollState.down || scrollState.up) && !hasInteractedRef.current) {
      setShowScrollHint(true);
    }
    if (!scrollState.down && !scrollState.up) {
      setShowScrollHint(false);
    }
  }, [scrollState.down, scrollState.up]);

  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;

    const handleFirstInteraction = () => {
      hasInteractedRef.current = true;
      setShowScrollHint(false);
    };

    el.addEventListener('pointerdown', handleFirstInteraction, { once: true });
    el.addEventListener('touchstart', handleFirstInteraction, { once: true });
    el.addEventListener('wheel', handleFirstInteraction, { once: true, passive: true });

    return () => {
      el.removeEventListener('pointerdown', handleFirstInteraction);
      el.removeEventListener('touchstart', handleFirstInteraction);
      el.removeEventListener('wheel', handleFirstInteraction);
    };
  }, []);

  // Removed custom dragging to enable native responsive scrolling
  useEffect(() => {
    // Keep it entirely native
    return undefined;
  }, []);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      minHeight: 500,
      maxHeight: 600,
      background: 'linear-gradient(135deg, #f6f8fb 0%, #e9eef5 100%)',
      borderRadius: 24,
      padding: '10px 0'
    }}>
      <div style={{
        width: 300,
        maxWidth: '100%',
        height: 540,
        borderRadius: 36,
        background: 'linear-gradient(180deg, #2c2c2e, #1c1c1e)',
        border: '6px solid #121214',
        boxShadow: '0 20px 40px rgba(0,0,0,0.12), 0 8px 16px rgba(0,0,0,0.08), inset 0 0 0 2px rgba(255,255,255,0.1)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
        transform: 'rotate(0deg)'
      }}>
        {/* Volume Buttons */}
        <div style={{
          position: 'absolute',
          left: -8,
          top: '15%',
          width: 4,
          height: 50,
          background: '#121214',
          borderRadius: '2px 0 0 2px'
        }}></div>
        <div style={{
          position: 'absolute',
          left: -8,
          top: '25%',
          width: 4,
          height: 50,
          background: '#121214',
          borderRadius: '2px 0 0 2px'
        }}></div>

        {/* Power Button */}
        <div style={{
          position: 'absolute',
          right: -8,
          top: '20%',
          width: 4,
          height: 60,
          background: '#121214',
          borderRadius: '0 2px 2px 0'
        }}></div>

        {/* Home Indicator */}
        <div style={{
          position: 'absolute',
          bottom: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 140,
          height: 4,
          background: '#ffffff',
          borderRadius: 2,
          opacity: 0.2,
          zIndex: 1
        }}></div>

        {/* Phone Screen */}
        <div style={{
          width: 288,
          height: 500,
          borderRadius: 28,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: '#ece5dd',
          boxSizing: 'border-box',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>

          {/* Status Bar */}
          <div style={{
            background: '#075e54',
            height: 24,
            display: 'flex',
            alignItems: 'center',
            padding: '0 14px',
            justifyContent: 'space-between',
            fontSize: 12,
            color: 'rgba(255,255,255,0.9)',
            fontWeight: 500,
            position: 'relative',
            zIndex: 2
          }}>
            <div>9:41</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
              <div>5G</div>
              {/* Signal Strength */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 1,
                height: 10,
                paddingTop: 2
              }}>
                <div style={{ width: 2, height: '30%', background: 'currentColor' }} />
                <div style={{ width: 2, height: '60%', background: 'currentColor' }} />
                <div style={{ width: 2, height: '80%', background: 'currentColor' }} />
                <div style={{ width: 2, height: '100%', background: 'currentColor' }} />
              </div>
              {/* Battery */}
              <div style={{
                width: 18,
                height: 9,
                padding: 1,
                border: '1.5px solid currentColor',
                borderRadius: 2,
                position: 'relative',
                marginTop: 1
              }}>
                <div style={{
                  position: 'absolute',
                  right: -3,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 2,
                  height: 4,
                  background: 'currentColor',
                  borderRadius: '0 1px 1px 0'
                }} />
                <div style={{
                  width: '88%',
                  height: '100%',
                  background: 'currentColor',
                  borderRadius: 1
                }} />
              </div>
            </div>
          </div>

          {/* Header */}
          <div style={{
            background: '#075e54',
            color: '#fff',
            padding: '8px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              background: '#128C7E',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              color: '#fff',
            }}>C</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontSize: 16 }}>Customer</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>online</div>
            </div>
          </div>

          {/* Chat Area: Native scrolling forced on */}
          <div ref={chatAreaRef} style={{
            flex: 1,
            background: 'linear-gradient(180deg, #efeae2 0%, #d2dbdc 100%)',
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            overflowY: 'scroll',
            overscrollBehavior: 'contain',
            scrollBehavior: 'smooth',
            cursor: 'default',
            WebkitOverflowScrolling: 'touch',
            userSelect: 'text',
            position: 'relative',
            backgroundImage: `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAApgAAAKYB3X3/OAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAACYSURBVEiJ7ZbBCcMwDEVfKYM4Q3YsSKF0ilwzSx0yo7lQKHiI7DpWGkgOfvAQSPr6WvZnG+zzxxw4gAtwB0IRD2BbmF8AE/Bo5Mc5RXGrpcxrEwmshnIpmgJEcauRR3GrpczFRymj9QwWIzcVDMMlxegpuJful2Ik//K1M4mvwGtV8A5s0dwadvvPPQM34JAyfwI7/oEPx8ZWdGcGxWYAAAAASUVORK5CYII=")`,
            backgroundSize: '24px'
          }}>
            {/* Unified Template Card */}
            <div style={{
              alignSelf: 'flex-start',
              maxWidth: '85%',
              background: '#fff',
              borderRadius: '12px 12px 12px 0',
              boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
              overflow: 'hidden',
              flexShrink: 0 /* CRITICAL: Prevent shrinking so scroll triggers */
            }}>
              {/* Header media: real URL, or placeholder when type comes from Meta (no public URL in API) */}
              {showMediaFromLink && mediaKind === 'image' && (
                <img src={media.link} alt="header media" style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: 180,
                  objectFit: 'cover',
                  display: 'block'
                }} />
              )}

              {showMediaFromLink && mediaKind === 'video' && (
                <video controls style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: 180,
                  objectFit: 'cover',
                  display: 'block',
                  backgroundColor: '#000'
                }}>
                  <source src={media.link} type="video/mp4" />
                </video>
              )}

              {showMediaFromLink && mediaKind === 'document' && (
                <div style={{
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  borderBottom: '1px solid rgba(0,0,0,0.1)'
                }}>
                  <div style={{ fontSize: 20 }}>📄</div>
                  <div style={{ fontSize: 12, color: '#667781' }}>Document</div>
                </div>
              )}

              {showMediaFromLink && mediaKind === 'audio' && (
                <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                  <audio controls src={media.link} style={{ width: '100%', height: 32 }} />
                </div>
              )}

              {showMediaPlaceholder && <MediaHeaderPlaceholder kind={mediaKind} />}

              {/* Header Text */}
              {header && (
                <div style={{
                  padding: '12px',
                  fontWeight: 600,
                  fontSize: 16,
                  color: '#111b21',
                  borderBottom: body || footer || (buttons && buttons.length > 0) ? '1px solid rgba(0,0,0,0.1)' : 'none'
                }}>
                  {fillVariables(header)}
                </div>
              )}

              {/* Body Text */}
              {body && (
                <div style={{
                  padding: '12px',
                  borderBottom: footer || (buttons && buttons.length > 0) ? '1px solid rgba(0,0,0,0.1)' : 'none'
                }}>
                  <div style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.4,
                    fontSize: 14,
                    color: '#111b21'
                  }}>{bodyText}</div>
                </div>
              )}

              {/* Footer Text */}
              {footer && (
                <div style={{
                  padding: '12px',
                  fontSize: 12,
                  color: '#667781',
                  borderBottom: (buttons && buttons.length > 0) ? '1px solid rgba(0,0,0,0.1)' : 'none'
                }}>
                  {footer}
                </div>
              )}

              {/* Interactive Buttons */}
              {Array.isArray(buttons) && buttons.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {buttons.slice(0, 3).map((b, i) => (
                    <button key={i} style={{
                      background: 'transparent',
                      border: 'none',
                      borderBottom: i < buttons.length - 1 ? '1px solid rgba(0,0,0,0.1)' : 'none',
                      color: b.type === 'URL' ? '#008069' : b.type === 'PHONE_NUMBER' ? '#008069' : '#111b21',
                      padding: '12px',
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      {b.type === 'PHONE_NUMBER' && '📞 '}
                      {b.type === 'URL' && '🔗 '}
                      {b.text || `Button ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {showScrollHint && (
              <div style={{
                position: 'absolute',
                bottom: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 999,
                fontSize: 12,
                color: '#fff',
                background: 'rgba(0,0,0,0.55)',
                boxShadow: '0 6px 18px rgba(0,0,0,0.28)',
                pointerEvents: 'none',
                letterSpacing: 0.2,
                transition: 'opacity 0.2s ease',
                zIndex: 3
              }}>
                <span style={{ fontSize: 14 }}>⬆⬇</span>
                <span>Drag or scroll to view more</span>
              </div>
            )}
          </div>

          {/* Scroll Helpers */}
          {(scrollState.up || scrollState.down) && (
            <div style={{
              position: 'absolute',
              right: 18,
              top: 96,
              display: 'flex',
              flexDirection: 'column',
              gap: 6
            }}>
              <button
                type="button"
                onClick={() => scrollByAmount(-160)}
                disabled={!scrollState.up}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  border: 'none',
                  background: scrollState.up ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.2)',
                  color: '#fff',
                  cursor: scrollState.up ? 'pointer' : 'default',
                  fontSize: 16,
                  lineHeight: '28px'
                }}
                aria-label="Scroll up preview"
              >^</button>
              <button
                type="button"
                onClick={() => scrollByAmount(160)}
                disabled={!scrollState.down}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  border: 'none',
                  background: scrollState.down ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.2)',
                  color: '#fff',
                  cursor: scrollState.down ? 'pointer' : 'default',
                  fontSize: 16,
                  lineHeight: '28px'
                }}
                aria-label="Scroll down preview"
              >v</button>
            </div>
          )}

          {/* Input Bar */}
          <div style={{
            background: '#f0f2f5',
            borderTop: '1px solid rgba(0,0,0,0.1)',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0
          }}>
            <div style={{
              fontSize: 20,
              color: '#54656f',
              cursor: 'pointer'
            }}>😊</div>
            <div style={{
              flex: 1,
              background: '#fff',
              border: '1px solid #e6e6e6',
              borderRadius: 20,
              height: 40,
              padding: '0 12px',
              display: 'flex',
              alignItems: 'center',
              fontSize: 14,
              color: '#3b4a54',
              boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)'
            }}>Type a message</div>
            <div style={{
              width: 40,
              height: 40,
              background: '#00a884',
              borderRadius: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 20,
              cursor: 'pointer',
              boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)'
            }}>🎤</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview;
